import React, { useState, useEffect } from 'react';

const TeamStats = ({ competitionId, statsFeed, onError }) => {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [playerStats, setPlayerStats] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('batting');
  const feedURL = process.env.REACT_APP_FEED_URL;

  const battingHeaders = [
    'Player', 'M', 'I', 'R', 'B', 'HS', 'NO', 'AVG', 'SR', '50s',
    '100s', 'DB%', 'BDRY%'
  ];

  const bowlingHeaders = [
    'Player', 'M', 'I', 'O', 'R', 'MD', 'WKTS', 'AVG', 'SR', 'ECON',
    'NB', 'WB', 'DB%', 'BDRY%'
  ];

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const defaultFeed = `${feedURL}`;

        const timestamp = new Date().getTime();
        setLoading(true);

        // Fetch the team data
        const response = await fetch(`${statsFeed || defaultFeed}/stats/${competitionId}-teamlist.js?onteamlist=_jqjsp&_=${timestamp}`);
        const data = await response.text();

        console.log('Raw team list response:', data); // Debugging log

        // Extract JSON from the JSONP response
        const matches = data.match(/onteamlist\((.*?)\);?$/s);
        if (!matches || !matches[1]) {
          throw new Error('Invalid team data format');
        }

        // Parse the JSON string
        const jsonStr = matches[1].trim();
        const parsedData = JSON.parse(jsonStr);

        console.log('Parsed team data:', parsedData); // Debugging log

        // Validate the parsed data structure
        if (!parsedData?.Teamlist) {
          throw new Error('Invalid team data structure');
        }

        // Map the team list to the desired format
        const teamList = parsedData.Teamlist.map(item => ({
          TeamID: item.TeamID,
          TeamName: item.TeamName,
          TeamLogo: item.TeamLogo,
        }));

        console.log('Mapped Team List:', teamList); // Debugging log

        setTeams(teamList);

        // Set the first team as the default selected team
        if (teamList.length > 0) {
          setSelectedTeam(teamList[0].TeamID);
        }
      } catch (err) {
        const errorMessage = 'Failed to load teams. Please try again later.';
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
        console.error('Error fetching teams:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, [competitionId, statsFeed, onError]);


  useEffect(() => {
    const fetchTeamStats = async () => {
      if (!selectedTeam) return;

      try {
        setLoading(true);
        const timestamp = new Date().getTime();
        const defaultFeed = `${feedURL}`;

        const response = await fetch(`${statsFeed || defaultFeed}/stats/${competitionId}-teamoverallstats.js?callback=onteamoverallstats&_=${timestamp}`);

        if (!response.ok) {
          console.log('API response not OK');
          setTeamStats(null);
          return;
        }
        const data = await response.text();

        const matches = data.match(/onteamoverallstats\((.*?)\);?$/s);
        if (!matches || !matches[1]) {
          console.log('No valid data format found');
          setTeamStats(null);
          return;
        }
        const jsonStr = matches[1].trim();

        const parsedData = JSON.parse(jsonStr);

        if (!parsedData?.teamstats) {
          console.log('No team stats found in response');
          setTeamStats(null);
          return;
        }

        const teamData = parsedData.teamstats.find(stat => String(stat.TeamID) === String(selectedTeam));
        if (!teamData) {
          setTeamStats(null);
          return;
        }

        setTeamStats(teamData);
      } catch (err) {
        console.error('Error fetching team stats:', err);
        setTeamStats(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamStats();
  }, [selectedTeam, competitionId, statsFeed, onError]);

  useEffect(() => {
    const fetchPlayerStats = async () => {
      if (!selectedTeam) {
        setPlayerStats([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const timestamp = new Date().getTime();
        const defaultFeed = `${feedURL}`;

        const response = await fetch(`${statsFeed || defaultFeed}/stats/${competitionId}-${selectedTeam}-playerstats.js?callback=onplayerstats&_=${timestamp}`);
        // Handle 404 and other HTTP errors
        if (!response.ok) {
          console.log(`HTTP error! status: ${response.status}`);
          setPlayerStats([]);
          return;
        }

        const data = await response.text();
        // Check for empty response
        if (!data || data.trim() === '') {
          console.log('Empty response received');
          setPlayerStats([]);
          return;
        }
        // Extract JSON from JSONP response
        const matches = data.match(/onplayerstats\((.*?)\);?$/s);
        if (!matches || !matches[1]) {
          console.log('Invalid data format received');
          setPlayerStats([]);
          return;
        }

        const jsonStr = matches[1].trim();

        // Check for empty JSON string
        if (!jsonStr) {
          console.log('Empty JSON string');
          setPlayerStats([]);
          return;
        }

        let parsedData;
        try {
          parsedData = JSON.parse(jsonStr);
        } catch (parseError) {
          console.log('JSON parse error:', parseError);
          setPlayerStats([]);
          return;
        }

        // Check for null or invalid playerstats
        if (!parsedData?.playerstats || !Array.isArray(parsedData.playerstats)) {
          console.log('Invalid or null player stats structure');
          setPlayerStats([]);
          return;
        }

        setPlayerStats(parsedData.playerstats);
      } catch (err) {
        const errorMessage = 'Failed to load player statistics. Please try again later.';
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
        console.error('Error fetching player stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlayerStats();
  }, [competitionId, selectedTeam, statsFeed, onError]);

  const handleTeamChange = (event) => {
    const teamId = parseInt(event.target.value, 10);
    setSelectedTeam(teamId);
    setSelectedTeamId(teamId); // Update selectedTeamId for player stats
  };

  if (loading && !teamStats) {
    return (
      <div className="w-full p-4 text-center">
        <div className="animate-pulse text-gray-600">Loading team data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  const statsItems = [
    { label: 'Matches', value: teamStats?.Matches || 0 },
    { label: 'Won', value: teamStats?.Wins || 0 },
    { label: 'Lost', value: teamStats?.Loss || 0 },
    { label: 'No Result', value: teamStats?.Aban || 0 },
    { label: 'Runs Scored', value: teamStats?.RunsScored || 0 },
    { label: 'Runs Conceded', value: teamStats?.RunsConceded || 0 },
    { label: 'Wickets Lost', value: teamStats?.WicketLost || 0 },
    { label: 'Wickets Taken', value: teamStats?.WicketsTaken || 0 },
    { label: 'Points', value: teamStats?.Points || 0 }
  ];

  // Conditional stats items based on match type and availability
  if (teamStats?.Fifties !== undefined) {
    statsItems.push({ label: "50's", value: teamStats.Fifties });
  }

  if (teamStats?.Centuries !== undefined) {
    statsItems.push({ label: "100's", value: teamStats.Centuries });
  }

  if (teamStats?.Partnership100 !== undefined) {
    statsItems.push({ label: "100+ Pship's", value: teamStats.Partnership100 });
  }

  if (teamStats?.BowlingExtras !== undefined) {
    statsItems.push({ label: 'Extras', value: teamStats.BowlingExtras });
  }

  const battingPlayers = [...playerStats]
    .sort((a, b) => (b.RunsScored || 0) - (a.RunsScored || 0));
  const bowlingPlayers = [...playerStats]
    .sort((a, b) => (b.WicketsTaken || 0) - (a.WicketsTaken || 0));

  return (
    <div id="teamStatsWrapper" className="">
      <div className="statsHeader">
        <div className="mcContainer">
          <div className="col s12 m12 l12">
            <select
              className="mcSelectDefault teamListFilter teamstatsFilter"
              value={selectedTeam || ''}
              onChange={handleTeamChange}
            >
              {teams.map(team => (
                <option key={team.TeamID} value={team.TeamID}>
                  {team.TeamName}
                </option>
              ))}
            </select>

            {teamStats && (
              <ul className="statsList">
                {statsItems.map((stat, index) => (
                  <li key={index}>
                    <div className="displayTable">
                      <div className="displayTableCell">
                        <span>{stat.label} </span>
                        <i>{stat.value}</i>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="statsContent">
        <div className="mcContainer">
          {(!playerStats || playerStats.length === 0) ? (
            <div className="no-stats-message" style={{ color: 'white' }}>No stats found</div>
          ) : (
            <div className="col s12 m12 l12">
              {/* Tabs */}
              <ul className="mcTabs roleTab teamTab">
                <li
                  className={`mcTabLink ${activeTab === 'batting' ? 'current' : ''}`}
                  onClick={() => setActiveTab('batting')}
                >
                  <span>Batting Stats</span>
                </li>
                <li
                  className={`mcTabLink ${activeTab === 'bowling' ? 'current' : ''}`}
                  onClick={() => setActiveTab('bowling')}
                >
                  <span>Bowling Stats</span>
                </li>
              </ul>

              {/* Batting Stats Table */}
              <div
                id="mcTeamBattingStats"
                className={`tabTeamContent ${activeTab === 'batting' ? 'current' : ''}`}
              >
                <table className="mcTableDefault batCardTable" cellSpacing="0" cellPadding="0" border="0">
                  <thead>
                    <tr>
                      {battingHeaders.map((header, index) => (
                        <th key={index} data-key={index}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {battingPlayers.map((player, index) => (
                      <tr key={index}>
                        <td>{player.PlayerName}</td>
                        <td>{player.Matches}</td>
                        <td>{player.Innings}</td>
                        <td>{player.RunsScored}</td>
                        <td>{player.BallsFaced}</td>
                        <td>{player.HighestScore}</td>
                        <td>{player.NotOuts}</td>
                        <td>{player.BattingAverage}</td>
                        <td>{player.StrikeRate || '0'}</td>
                        <td>{player.Fifties}</td>
                        <td>{player.Hundreds}</td>
                        <td>{player.DBPercent}</td>
                        <td>{player.BdryPercent}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Bowling Stats Table */}
              <div
                id="mcTeamBowlingStats"
                className={`tabTeamContent ${activeTab === 'bowling' ? 'current' : ''}`}
              >
                <table className="mcTableDefault bowCardTable" cellSpacing="0" cellPadding="0" border="0">
                  <thead>
                    <tr>
                      {bowlingHeaders.map((header, index) => (
                        <th key={index} data-key={index}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bowlingPlayers.map((player, index) => (
                      <tr key={index}>
                        <td>{player.PlayerName}</td>
                        <td>{player.Matches}</td>
                        <td>{player.InningsBowled}</td>
                        <td>{player.OversBowled}</td>
                        <td>{player.TotalRunsConceeded}</td>
                        <td>{player.Maidens}</td>
                        <td>{player.WicketsTaken}</td>
                        <td>{player.BowlingAverage}</td>
                        <td>{player.BowlingSR}</td>
                        <td>{player.EconomyRate || '0'}</td>
                        <td>{player.NoBalls}</td>
                        <td>{player.Wides}</td>
                        <td>{player.BowlingDotBallPercent}</td>
                        <td>{player.BowlingBdryPercentage}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default TeamStats;